import signupService from "@/services/signup.service";

const getInitialState = () => ({
  currentStep: 1,

  businessLicenseFile: null,
  pageLogoFile: null,

  ocrData: null,

  verificationResult: null,

  formData: {
    businessNumber: "",
    hospitalName: "",
    representativeName: "",
    address: {
      city: "",
      district: "",
      dong: "",
      detail: "",
    },
    // 이용약관 동의 상태
    agreement: {
      serviceTerms: false,
      privacyPolicy: false,
      marketingConsent: false,
    },
    username: "",
    password: "",
    managerName: "",
    managerPhone: "",
    medicalDepartments: [],
    country: "ko",
  },

  ocrBusinessCategories: [],
  ocrBusinessTypes: [],

  isLoading: false,
  errorMessage: "",

  signupResult: null,
});

export default {
  namespaced: true,

  state: getInitialState(),

  mutations: {
    SET_CURRENT_STEP(state, step) {
      state.currentStep = step;
    },

    SET_BUSINESS_LICENSE_FILE(state, file) {
      state.businessLicenseFile = file;
    },

    SET_PAGE_LOGO_FILE(state, file) {
      state.pageLogoFile = file;
    },

    SET_OCR_DATA(state, data) {
      state.ocrData = data;
      if (data) {
        state.ocrBusinessCategories = data.businessCategories || [];
        state.ocrBusinessTypes = data.businessTypes || [];
      }
    },

    SET_VERIFICATION_RESULT(state, result) {
      state.verificationResult = result;
    },

    SET_FORM_DATA(state, data) {
      state.formData = { ...state.formData, ...data };
    },

    SET_FORM_ADDRESS(state, address) {
      state.formData.address = { ...state.formData.address, ...address };
    },

    SET_LOADING(state, isLoading) {
      state.isLoading = isLoading;
    },

    SET_ERROR_MESSAGE(state, message) {
      state.errorMessage = message;
    },

    SET_SIGNUP_RESULT(state, result) {
      state.signupResult = result;
    },

    // 동의 항목 상태 업데이트
    SET_AGREEMENT(state, agreement) {
      state.formData.agreement = { ...state.formData.agreement, ...agreement };
    },

    APPLY_OCR_TO_FORM(state) {
      if (!state.ocrData) return;

      state.formData = {
        ...state.formData,
        businessNumber: state.ocrData.businessNumber?.replace(/-/g, "") || "",
        hospitalName: state.ocrData.companyName || "",
        representativeName: state.ocrData.representativeName || "",
        address: {
          ...state.formData.address,
          city: state.ocrData.address?.city || "",
          district: state.ocrData.address?.district || "",
          dong: state.ocrData.address?.dong || "",
          detail: state.ocrData.address?.full || "",
        },
      };
    },

    CLEAR_STORE(state) {
      Object.assign(state, getInitialState());
    },
  },

  actions: {
    async processOcr({ commit, state }) {
      if (!state.businessLicenseFile) {
        commit("SET_ERROR_MESSAGE", "사업자등록증 파일을 선택해주세요.");
        return { success: false };
      }

      const businessNumber = state.formData.businessNumber?.replace(/-/g, "");
      if (!businessNumber) {
        commit("SET_ERROR_MESSAGE", "사업자번호가 필요합니다.");
        return { success: false };
      }

      commit("SET_LOADING", true);
      commit("SET_ERROR_MESSAGE", "");

      try {
        const result = await signupService.ocrBusinessLicense(
          state.businessLicenseFile,
          businessNumber
        );

        if (result.success) {
          commit("SET_OCR_DATA", result.data);
          return { success: true, data: result.data };
        } else {
          commit(
            "SET_ERROR_MESSAGE",
            result.errorMessage || "OCR 처리에 실패했습니다."
          );
          return { success: false, errorMessage: result.errorMessage };
        }
      } catch (error) {
        const errorMsg =
          error.response?.data?.message || "OCR 처리 중 오류가 발생했습니다.";
        commit("SET_ERROR_MESSAGE", errorMsg);
        return { success: false, errorMessage: errorMsg };
      } finally {
        commit("SET_LOADING", false);
      }
    },

    async verifyBusinessNumber({ commit, state }) {
      const businessNumber = state.formData.businessNumber;
      if (!businessNumber) {
        commit("SET_ERROR_MESSAGE", "사업자번호를 입력해주세요.");
        return { success: false };
      }

      commit("SET_LOADING", true);
      commit("SET_ERROR_MESSAGE", "");

      try {
        const result = await signupService.verifyBusinessNumber(businessNumber);

        if (result.code === -1) {
          const errorMsg = result.message || "사업자번호 검증에 실패했습니다.";
          commit("SET_ERROR_MESSAGE", errorMsg);
          return { success: false, errorMessage: errorMsg };
        }

        commit("SET_VERIFICATION_RESULT", {
          canRegister: result.canRegister,
          reason: result.reason,
          canOverwrite: result.existingRegistration?.canOverwrite || false,
          existingCompanyId: result.existingRegistration?.companyId,
          status: result.status,
        });

        if (!result.canRegister) {
          commit(
            "SET_ERROR_MESSAGE",
            result.reason || "고객센터를 통해 가입이 가능한 사업자입니다. 고객센터로 문의해주세요."
          );
          return { success: false, reason: result.reason };
        }

        return { success: true, data: result };
      } catch (error) {
        const errorMsg =
          error.response?.data?.message ||
          "사업자번호 검증 중 오류가 발생했습니다.";
        commit("SET_ERROR_MESSAGE", errorMsg);
        return { success: false, errorMessage: errorMsg };
      } finally {
        commit("SET_LOADING", false);
      }
    },

    async submitSignup({ commit, state }) {
      commit("SET_LOADING", true);
      commit("SET_ERROR_MESSAGE", "");

      try {
        const businessNumber = state.formData.businessNumber.replace(/-/g, "");

        const signupData = {
          businessNumber,
          country: state.formData.country,
          hospitalName: state.formData.hospitalName,
          representativeName: state.formData.representativeName,
          username: state.formData.username,
          password: state.formData.password,
          managerName: state.formData.managerName,
          managerPhone: state.formData.managerPhone,
          medicalDepartments: state.formData.medicalDepartments,
          address: {
            city: state.formData.address.city,
            district: state.formData.address.district,
            dong: state.formData.address.dong,
            detail: state.formData.address.detail,
          },
          // 동의 항목 (회원가입 API 페이로드에 포함)
          agreement: {
            serviceTerms: state.formData.agreement.serviceTerms,
            privacyPolicy: state.formData.agreement.privacyPolicy,
            marketingConsent: state.formData.agreement.marketingConsent,
          },
        };

        if (
          state.ocrBusinessCategories.length > 0 ||
          state.ocrBusinessTypes.length > 0
        ) {
          signupData.ocrData = {
            businessCategories: state.ocrBusinessCategories,
            businessTypes: state.ocrBusinessTypes,
          };
        }

        const result = await signupService.signupHospital(
          signupData,
          state.businessLicenseFile
        );

        commit("SET_SIGNUP_RESULT", result);
        return { success: true, data: result };
      } catch (error) {
        const errorMsg =
          error.response?.data?.message || "회원가입 중 오류가 발생했습니다.";
        commit("SET_ERROR_MESSAGE", errorMsg);
        return { success: false, errorMessage: errorMsg };
      } finally {
        commit("SET_LOADING", false);
      }
    },

    nextStep({ commit, state }) {
      if (state.currentStep < 5) {
        commit("SET_CURRENT_STEP", state.currentStep + 1);
      }
    },

    prevStep({ commit, state }) {
      if (state.currentStep > 1) {
        commit("SET_CURRENT_STEP", state.currentStep - 1);
      }
    },

    goToStep({ commit }, step) {
      commit("SET_CURRENT_STEP", step);
    },

    applyOcrToForm({ commit }) {
      commit("APPLY_OCR_TO_FORM");
    },

    clearStore({ commit }) {
      commit("CLEAR_STORE");
    },
  },

  getters: {
    currentStep: (state) => state.currentStep,
    businessLicenseFile: (state) => state.businessLicenseFile,
    pageLogoFile: (state) => state.pageLogoFile,
    ocrData: (state) => state.ocrData,
    verificationResult: (state) => state.verificationResult,
    formData: (state) => state.formData,
    isLoading: (state) => state.isLoading,
    errorMessage: (state) => state.errorMessage,
    signupResult: (state) => state.signupResult,
    canRegister: (state) => state.verificationResult?.canRegister || false,
    canOverwrite: (state) => state.verificationResult?.canOverwrite || false,
    // 필수 동의 항목 충족 여부 (이용약관 + 개인정보 처리방침)
    agreementValid: (state) =>
      state.formData.agreement.serviceTerms &&
      state.formData.agreement.privacyPolicy,
    // 현재 동의 상태 전체 반환
    agreement: (state) => state.formData.agreement,
    hasOcrAddress: (state) =>
      !!(
        state.ocrData?.address?.city ||
        state.ocrData?.address?.district ||
        state.ocrData?.address?.dong ||
        state.ocrData?.address?.full
      ),
    hasOcrHospitalName: (state) => !!state.ocrData?.companyName,
    hasOcrRepresentativeName: (state) => !!state.ocrData?.representativeName,
  },
};
